'use client'

/**
 * Error Boundary para capturar errores de renderizado en React
 * Previene que toda la aplicación se rompa por un error en un componente
 */

import { Component, ReactNode } from 'react'
import Link from 'next/link'
import { logger } from '@/lib/logger'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log del error
    logger.error('React Error Boundary caught an error:', {
      error: error.toString(),
      componentStack: errorInfo.componentStack
    })

    // Reportar error por email (fire-and-forget, silencioso)
    try {
      fetch('/api/errors/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: typeof window !== 'undefined' ? window.location.href : '',
          error: {
            message: error.message || 'Sin mensaje',
            name: error.name || 'Error',
            stack: error.stack || 'Sin stack trace',
          },
          componentStack: errorInfo.componentStack,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          timestamp: new Date().toISOString(),
        }),
        keepalive: true,
      }).catch(() => {})
    } catch {
      // swallow
    }

    // Callback personalizado
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    this.setState({
      errorInfo
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  render() {
    if (this.state.hasError) {
      // Si hay un fallback personalizado, usarlo
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Fallback por defecto — minimalista, blanco y negro
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#fff',
          color: '#111',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          <div style={{ maxWidth: '380px', width: '100%' }}>
            <h1 style={{
              fontSize: '20px',
              fontWeight: 600,
              margin: '0 0 8px',
              letterSpacing: '-0.01em',
            }}>
              Algo salió mal
            </h1>
            <p style={{
              fontSize: '14px',
              color: '#666',
              margin: '0 0 24px',
              lineHeight: 1.5,
            }}>
              Ocurrió un error inesperado. Podés recargar la página o volver al inicio.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{ marginBottom: '20px' }}>
                <summary style={{
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#999',
                  marginBottom: '8px',
                }}>
                  Ver detalles técnicos
                </summary>
                <pre style={{
                  background: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontFamily: 'ui-monospace, monospace',
                  overflow: 'auto',
                  maxHeight: '180px',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#111',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Recargar página
              </button>
              <Link
                href="/"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'transparent',
                  color: '#111',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textAlign: 'center',
                  textDecoration: 'none',
                  boxSizing: 'border-box',
                }}
              >
                Ir al inicio
              </Link>
            </div>

            {this.props.children && (
              <button
                onClick={this.handleReset}
                style={{
                  display: 'block',
                  width: '100%',
                  marginTop: '12px',
                  padding: '8px',
                  background: 'transparent',
                  border: 'none',
                  color: '#999',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Intentar nuevamente sin recargar
              </button>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Hook para usar ErrorBoundary de forma más simple
 * Nota: Este es un wrapper funcional, el ErrorBoundary real debe ser un class component
 */
function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    )
  }
}
